import { Q } from '@nozbe/watermelondb'
import { difference } from 'lodash'
import { clearUnread, handleAppBadge } from 'services/api/userSettings'
import Database from 'services/db/Database'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import QA from 'services/QA'
import {
  ChangeDoc,
  ChangeDocsByTypes,
  DocType,
  EventDoc,
  GroupDoc,
  GroupInheritanceMap,
  ParticipantDataDoc,
  TaskType,
  UserSettingsDoc
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  docTypeToCollectionType,
  GROUP_MOST_RECENT_EVENT
} from 'utils/api/changes'
import { buildEditEventMessage } from 'utils/api/events'
import { updateGroupDocMessage } from 'utils/api/groups'
import { isGroupcalApp } from 'utils/appType'
import { eventCalendarIds, isEventInCalendar } from 'utils/calendarInheritance'
import { modelsToIdsObject } from 'utils/watermalon'

import { ParticipantsAll } from './Queries/GroupQueries'
import { generateDefaultReminder } from './utils/Event'

class ChangesService {
  public async updateDocsMessage(
    docsByTypes: ChangeDocsByTypes,
    ignoreEventsForMessage: boolean
  ): Promise<ChangeDocsByTypes> {
    const newDocs: ChangeDocsByTypes = {} as ChangeDocsByTypes

    const participants = await ParticipantsAll().fetch()

    async function updateDocsMessageByType<T>(
      docType: DocType,
      docs: Record<string, ChangeDoc>,
      transformer: (
        userID: string,
        newDoc: T,
        prevDoc: T,
        participants: Model<ParticipantDataDoc>[]
      ) => T
    ): Promise<Record<string, ChangeDoc>> {
      const docsIds = Object.keys(docs)

      const prevDocs = modelsToIdsObject(
        await Database.getCollection(docTypeToCollectionType(docType))
          .query(Q.where('_id', Q.oneOf(docsIds)))
          .fetch()
      )

      const docsMap: Record<string, any> = {}
      docsIds.forEach((docId) => {
        docsMap[docId] = transformer(
          LocalStorage.get(LocalStorageKey.USER_ID) as string,
          docs[docId] as unknown as T,
          prevDocs[docId] as unknown as T,
          participants
        )
      })
      return docsMap
    }

    await Promise.all(
      (Object.keys(docsByTypes) as DocType[]).map(async (key) => {
        if (key === DocType.GROUP) {
          newDocs[key] = await updateDocsMessageByType<GroupDoc>(
            DocType.GROUP,
            docsByTypes[key],
            updateGroupDocMessage
          )
        } else if (key === DocType.EVENT && ignoreEventsForMessage === false) {
          const record = await updateDocsMessageByType<EventDoc>(
            DocType.EVENT,
            docsByTypes[key],
            buildEditEventMessage
          )
          newDocs[key] = record
        } else {
          newDocs[key] = docsByTypes[key]
        }
      })
    )

    return newDocs
  }

  public saveChanges(docs: ChangeDocsByTypes): Promise<void> {
    return Database.write(async () => {
      const updateCollection = async (
        docType: DocType,
        collectionKey: CollectionType
      ) => {
        const docsFromServer = docs[docType]
        const allIdsFromServer = Object.keys(docsFromServer)
        const existingDocs = await Database.getCollection(collectionKey)
          .query(Q.where('_id', Q.oneOf(allIdsFromServer)))
          .fetch()

        const docsToUpdateIds = existingDocs.map((doc) => doc._id)
        const docsToSaveIds = allIdsFromServer.filter(
          (id) => !docsToUpdateIds.includes(id)
        )

        function shouldIgnoreField(doc: any, field: string): boolean {
          return (
            isGroupcalApp() &&
            docType === DocType.EVENT &&
            (doc as any).TaskType === TaskType.meet &&
            field === 'Reminder'
          )
        }
        const collection = Database.getCollection(collectionKey)

        Logger.blue('Changes:Saving:Existing', existingDocs)

        const batchToUpdate = [
          ...existingDocs.map((existingDoc) =>
            existingDoc.prepareUpdate((doc) => {
              const existingKeys = Object.keys(
                Database.toServerObject(existingDoc)
              )
              const arrivedKeys = Object.keys(
                Database.toServerObject(docsFromServer[existingDoc._id])
              )

              const diffFields = difference(existingKeys, arrivedKeys)
              Logger.blue('Changes:Diff', diffFields)

              Object.keys(docsFromServer[doc._id]).forEach((key) => {
                if (shouldIgnoreField(doc, key)) {
                  ;(doc as any)[key] = (existingDoc as any)[key]
                } else {
                  if (['_sortOrder', 'FirstDayOfTheWeek'].includes(key)) {
                    ;(doc as any)._raw[key] = docsFromServer[doc._id][key]
                  } else if (key !== 'id') {
                    ;(doc as any)[key] = docsFromServer[doc._id][key]
                  }
                }
              })

              existingKeys.forEach((key) => {
                if (
                  !arrivedKeys.includes(key) &&
                  !shouldIgnoreField(doc, key)
                ) {
                  ;(doc as any)[key] = undefined
                }
              })
            })
          ),
          ...docsToSaveIds.map((id) =>
            collection.prepareCreate((doc) => {
              for (
                let index = 0;
                index < Object.keys(docsFromServer[id]).length;
                index++
              ) {
                const key = Object.keys(docsFromServer[id])[index]

                if (shouldIgnoreField(doc, key)) continue

                if (['_sortOrder'].includes(key)) {
                  ;(doc as any)._raw[key] = docsFromServer[id][key]
                } else if (key !== 'id') {
                  if (key === 'isDeleted') {
                    ;(doc as any)[key] =
                      docsFromServer[id][key] === '1' ? '1' : '0'
                  } else if (
                    key === 'Status' ||
                    key === 'TaskType' ||
                    key === 'Priority'
                  ) {
                    ;(doc as any)[key] = String(docsFromServer[id][key])
                  }

                  ;(doc as any)[key] = docsFromServer[id][key]
                }
              }

              //for all incoming events we set default reminder
              if ((doc as any)['TaskType'] !== TaskType.personalEvent) {
                ;(doc as any)['Reminder'] = [
                  generateDefaultReminder(
                    (doc as any)['AllDay'] === '1',
                    (doc as any)['StartDate']
                  )
                ]
              }
            })
          )
        ]

        await Database.batch(batchToUpdate)

        if (QA.isDebugLogger()) {
          const updatedCollection = await Database.getCollection(collectionKey)
            .query()
            .fetch()

          Logger.debug('Updated collection', collectionKey, updatedCollection)
        }
      }

      await Promise.all(
        (Object.keys(docs) as DocType[]).map((key) =>
          updateCollection(key, docTypeToCollectionType(key))
        )
      )

      // Update group _sortOrder with most recent event.
      const groupsToUpdate = await Database.getCollection(CollectionType.GROUP)
        .query(Q.where('_id', Q.oneOf(Object.keys(GROUP_MOST_RECENT_EVENT))))
        .fetch()

      groupsToUpdate.forEach((_doc) =>
        _doc.update((doc) => {
          const currentSortOrderOfGroup = Number((_doc as any)['_sortOrder'])
          const recentEventLastUpdate = GROUP_MOST_RECENT_EVENT[_doc._id]
          if (recentEventLastUpdate > currentSortOrderOfGroup) {
            ;(doc as any)._raw['_sortOrder'] = recentEventLastUpdate
          }
        })
      )
    })
  }
}

export default new ChangesService()

export function populateUnreadIfNeeded(
  docs: ChangeDocsByTypes,
  userSettings: Model<UserSettingsDoc>
) {
  if (!userSettings) return

  const groupsSettings = userSettings.GroupsSettings ?? {}
  let shouldUpdate = false

  if (docs && docs.GroupEvent) {
    const hasCurrentGroupEvents = (
      Object.values(docs.GroupEvent) as EventDoc[]
    ).some((event) => {
      return isEventInCalendar(
        event,
        LocalStorage.get(LocalStorageKey.SELECTED_GROUP) ?? ''
      )
    })

    const eventsForUnread = (
      Object.values(docs.GroupEvent) as EventDoc[]
    ).filter((event) => {
      return (
        event.GroupID != LocalStorage.get(LocalStorageKey.SELECTED_GROUP) &&
        Number(event.LastUpdate) > Number(userSettings.LastUpdate) &&
        event.UserID != LocalStorage.get(LocalStorageKey.USER_ID)
      )
    })

    if (eventsForUnread) {
      eventsForUnread.forEach((event) => {
        const groupsToUpdate = eventCalendarIds(event)

        const eventId = event._id ?? ''

        for (const groupId of groupsToUpdate) {
          const groupSettingsForEvent = groupsSettings?.[groupId] ?? {}

          if (!groupSettingsForEvent.UnreadItems) {
            groupSettingsForEvent.UnreadItems = {}
          }

          if (!groupSettingsForEvent.UnreadItems[eventId]) {
            shouldUpdate = true
          }

          groupSettingsForEvent.UnreadItems[eventId] = '1'
          groupsSettings[groupId] = {
            ...groupsSettings[groupId],
            ...groupSettingsForEvent
          }
        }
      })
    }

    if (shouldUpdate) {
      Database.write(async () => {
        userSettings.update(() => {
          userSettings.GroupsSettings = { ...groupsSettings }

          handleAppBadge()
        })
      })
    }

    if (hasCurrentGroupEvents) {
      setTimeout(() => {
        const currentGroup = LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
        if (currentGroup) clearUnread(currentGroup)
      }, 2000)
    }
  }
}
